import type { ImagePickerAsset } from 'expo-image-picker';
import { Image as ExpoImage } from 'expo-image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  PanResponder,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  type ViewStyle,
  useWindowDimensions,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import {
  cropProfileImageAsset,
  PROFILE_COVER_CROP_ASPECT,
  type ProfileImageCropRect,
} from '@/src/features/profile/profile-image-picker';
import type { ProfileImageKind } from '@/src/features/profile/profile-service';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

type Offset = {
  x: number;
  y: number;
};

const DEFAULT_CROP_ZOOM = 1.1;
const MIN_CROP_ZOOM = 1;
const MAX_CROP_ZOOM = 3;
const ZOOM_STEP = 0.1;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function getPointFromEvent(event: unknown): Offset | null {
  const nativeEvent =
    typeof event === 'object' && event && 'nativeEvent' in event
      ? (event as { nativeEvent?: unknown }).nativeEvent
      : event;
  const eventRecord =
    typeof nativeEvent === 'object' && nativeEvent
      ? (nativeEvent as Record<string, unknown>)
      : {};
  const touches = eventRecord.touches as
    | ArrayLike<{ clientX?: number; clientY?: number; pageX?: number; pageY?: number }>
    | undefined;
  const changedTouches = eventRecord.changedTouches as
    | ArrayLike<{ clientX?: number; clientY?: number; pageX?: number; pageY?: number }>
    | undefined;
  const touch = touches?.[0] ?? changedTouches?.[0];
  const x =
    typeof eventRecord.clientX === 'number'
      ? eventRecord.clientX
      : typeof touch?.clientX === 'number'
        ? touch.clientX
        : typeof eventRecord.pageX === 'number'
          ? eventRecord.pageX
          : touch?.pageX;
  const y =
    typeof eventRecord.clientY === 'number'
      ? eventRecord.clientY
      : typeof touch?.clientY === 'number'
        ? touch.clientY
        : typeof eventRecord.pageY === 'number'
          ? eventRecord.pageY
          : touch?.pageY;

  if (typeof x !== 'number' || typeof y !== 'number') {
    return null;
  }

  return { x, y };
}

function stopWebEvent(event: unknown) {
  const maybeEvent = event as {
    preventDefault?: () => void;
    stopPropagation?: () => void;
  };

  maybeEvent.preventDefault?.();
  maybeEvent.stopPropagation?.();
}

function frameSizeForKind({
  kind,
  viewportWidth,
}: {
  kind: ProfileImageKind;
  viewportWidth: number;
}) {
  const aspect = kind === 'avatar' ? 1 : PROFILE_COVER_CROP_ASPECT;
  const maxWidth = kind === 'avatar' ? 420 : 720;
  const width = Math.min(Math.max(260, viewportWidth - 48), maxWidth);

  return {
    aspect,
    height: width / aspect,
    width,
  };
}

function cropRectFromState({
  asset,
  frameHeight,
  frameWidth,
  offset,
  zoom,
}: {
  asset: ImagePickerAsset;
  frameHeight: number;
  frameWidth: number;
  offset: Offset;
  zoom: number;
}): ProfileImageCropRect {
  const sourceWidth = Math.max(1, asset.width || frameWidth);
  const sourceHeight = Math.max(1, asset.height || frameHeight);
  const scale =
    Math.max(frameWidth / sourceWidth, frameHeight / sourceHeight) * zoom;
  const cropWidth = frameWidth / scale;
  const cropHeight = frameHeight / scale;
  const originX = (sourceWidth - cropWidth) / 2 - offset.x / scale;
  const originY = (sourceHeight - cropHeight) / 2 - offset.y / scale;

  return {
    height: Math.min(sourceHeight, cropHeight),
    originX: clamp(originX, 0, Math.max(0, sourceWidth - cropWidth)),
    originY: clamp(originY, 0, Math.max(0, sourceHeight - cropHeight)),
    width: Math.min(sourceWidth, cropWidth),
  };
}

export function ProfileImageCropperModal({
  asset,
  kind,
  onCancel,
  onComplete,
  visible,
}: {
  asset: ImagePickerAsset | null;
  kind: ProfileImageKind | null;
  onCancel: () => void;
  onComplete: (asset: ImagePickerAsset) => void;
  visible: boolean;
}) {
  const { width: viewportWidth } = useWindowDimensions();
  const [cropError, setCropError] = useState<string | null>(null);
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [cropping, setCropping] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_CROP_ZOOM);
  const maxOffsetRef = useRef<Offset>({ x: 0, y: 0 });
  const offsetRef = useRef<Offset>({ x: 0, y: 0 });
  const panStart = useRef<Offset>({ x: 0, y: 0 });
  const webDrag = useRef<{
    cleanup?: () => void;
    startOffset: Offset;
    startPoint: Offset;
  } | null>(null);
  const frame = useMemo(
    () =>
      frameSizeForKind({
        kind: kind ?? 'avatar',
        viewportWidth,
      }),
    [kind, viewportWidth],
  );
  const sourceWidth = Math.max(1, asset?.width || frame.width);
  const sourceHeight = Math.max(1, asset?.height || frame.height);
  const imageScale =
    Math.max(frame.width / sourceWidth, frame.height / sourceHeight) * zoom;
  const imageWidth = sourceWidth * imageScale;
  const imageHeight = sourceHeight * imageScale;
  const maxOffset = useMemo(
    () => ({
      x: Math.max(0, (imageWidth - frame.width) / 2),
      y: Math.max(0, (imageHeight - frame.height) / 2),
    }),
    [frame.height, frame.width, imageHeight, imageWidth],
  );

  const commitOffset = useCallback((nextOffset: Offset) => {
    const bounds = maxOffsetRef.current;
    const clampedOffset = {
      x: clamp(nextOffset.x, -bounds.x, bounds.x),
      y: clamp(nextOffset.y, -bounds.y, bounds.y),
    };

    offsetRef.current = clampedOffset;
    setOffset(clampedOffset);
  }, []);

  useEffect(() => {
    const nextOffset = { x: 0, y: 0 };

    offsetRef.current = nextOffset;
    setOffset(nextOffset);
    setCropError(null);
    setZoom(DEFAULT_CROP_ZOOM);
  }, [asset?.uri, kind]);

  useEffect(() => {
    maxOffsetRef.current = maxOffset;
    setOffset((current) => {
      const clampedOffset = {
        x: clamp(current.x, -maxOffset.x, maxOffset.x),
        y: clamp(current.y, -maxOffset.y, maxOffset.y),
      };

      offsetRef.current = clampedOffset;

      return clampedOffset;
    });
  }, [maxOffset]);

  useEffect(
    () => () => {
      webDrag.current?.cleanup?.();
    },
    [],
  );

  const handleWebPointerDown = useCallback(
    (event: unknown) => {
      if (Platform.OS !== 'web') {
        return;
      }

      const point = getPointFromEvent(event);

      if (!point) {
        return;
      }

      stopWebEvent(event);
      webDrag.current?.cleanup?.();

      const handleMove = (moveEvent: unknown) => {
        const movePoint = getPointFromEvent(moveEvent);
        const activeDrag = webDrag.current;

        if (!movePoint || !activeDrag) {
          return;
        }

        stopWebEvent(moveEvent);
        commitOffset({
          x: activeDrag.startOffset.x + movePoint.x - activeDrag.startPoint.x,
          y: activeDrag.startOffset.y + movePoint.y - activeDrag.startPoint.y,
        });
      };

      const handleEnd = (endEvent: unknown) => {
        stopWebEvent(endEvent);
        webDrag.current?.cleanup?.();
        webDrag.current = null;
      };

      const cleanup = () => {
        window.document.removeEventListener('pointermove', handleMove);
        window.document.removeEventListener('pointerup', handleEnd);
        window.document.removeEventListener('pointercancel', handleEnd);
      };

      window.document.addEventListener('pointermove', handleMove, {
        passive: false,
      });
      window.document.addEventListener('pointerup', handleEnd, {
        passive: false,
      });
      window.document.addEventListener('pointercancel', handleEnd, {
        passive: false,
      });

      webDrag.current = {
        cleanup,
        startOffset: offsetRef.current,
        startPoint: point,
      };
    },
    [commitOffset],
  );

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onStartShouldSetPanResponderCapture: () => true,
        onMoveShouldSetPanResponder: (_, gesture) =>
          Math.abs(gesture.dx) > 2 || Math.abs(gesture.dy) > 2,
        onMoveShouldSetPanResponderCapture: (_, gesture) =>
          Math.abs(gesture.dx) > 2 || Math.abs(gesture.dy) > 2,
        onPanResponderGrant: () => {
          panStart.current = offsetRef.current;
        },
        onPanResponderMove: (_, gesture) => {
          commitOffset({
            x: clamp(
              panStart.current.x + gesture.dx,
              -maxOffset.x,
              maxOffset.x,
            ),
            y: clamp(
              panStart.current.y + gesture.dy,
              -maxOffset.y,
              maxOffset.y,
            ),
          });
        },
        onPanResponderTerminationRequest: () => false,
        onShouldBlockNativeResponder: () => true,
      }),
    [commitOffset, maxOffset.x, maxOffset.y],
  );
  const cropFrameHandlers =
    Platform.OS === 'web'
      ? ({ onPointerDown: handleWebPointerDown } as Record<string, unknown>)
      : panResponder.panHandlers;

  async function handleUseCrop() {
    if (!asset || !kind) {
      return;
    }

    setCropping(true);
    setCropError(null);

    try {
      const croppedAsset = await cropProfileImageAsset({
        asset,
        crop: cropRectFromState({
          asset,
          frameHeight: frame.height,
          frameWidth: frame.width,
          offset,
          zoom,
        }),
        kind,
      });

      onComplete(croppedAsset);
    } catch {
      setCropError(
        'This photo format could not be cropped here. You can still upload it and Frienzzi will process it automatically.',
      );
    } finally {
      setCropping(false);
    }
  }

  function handleUploadWithoutCrop() {
    if (!asset) {
      return;
    }

    onComplete(asset);
  }

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onCancel}
    >
      <Pressable style={styles.backdrop} onPress={onCancel}>
        <Pressable
          style={styles.panel}
          onPress={(event) => event.stopPropagation()}
        >
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>
                Crop {kind === 'cover' ? 'cover photo' : 'profile photo'}
              </Text>
              <Text style={styles.subtitle}>
                Drag the image and adjust zoom before saving.
              </Text>
            </View>
            <Pressable
              accessibilityRole="button"
              onPress={onCancel}
              style={styles.closeButton}
            >
              <Text style={styles.closeText}>x</Text>
            </Pressable>
          </View>

          <View
            {...cropFrameHandlers}
            style={[
              styles.cropFrame,
              Platform.OS === 'web' ? styles.webCropFrame : null,
              {
                height: frame.height,
                width: frame.width,
              },
            ]}
          >
            {asset ? (
              <ExpoImage
                contentFit="fill"
                onError={() => {
                  setCropError(
                    'This photo format could not be previewed here. You can still upload it and Frienzzi will process it automatically.',
                  );
                }}
                pointerEvents="none"
                source={{ uri: asset.uri }}
                style={[
                  styles.cropImage,
                  {
                    height: imageHeight,
                    left: (frame.width - imageWidth) / 2 + offset.x,
                    top: (frame.height - imageHeight) / 2 + offset.y,
                    width: imageWidth,
                  },
                ]}
              />
            ) : null}
            <View pointerEvents="none" style={styles.cropBorder} />
          </View>

          {cropError ? (
            <View style={styles.fallbackNotice}>
              <Text style={styles.fallbackText}>{cropError}</Text>
              <Button
                title="Upload without crop"
                variant="secondary"
                disabled={cropping}
                onPress={handleUploadWithoutCrop}
                style={styles.fallbackButton}
              />
            </View>
          ) : null}

          <View style={styles.zoomRow}>
            <Button
              title="-"
              variant="secondary"
              onPress={() =>
                setZoom((value) => Math.max(MIN_CROP_ZOOM, value - ZOOM_STEP))
              }
              style={styles.zoomButton}
            />
            <Text style={styles.zoomText}>{Math.round(zoom * 100)}%</Text>
            <Button
              title="+"
              variant="secondary"
              onPress={() =>
                setZoom((value) => Math.min(MAX_CROP_ZOOM, value + ZOOM_STEP))
              }
              style={styles.zoomButton}
            />
          </View>

          <View style={styles.actions}>
            <Button
              title="Cancel"
              variant="secondary"
              disabled={cropping}
              onPress={onCancel}
              style={styles.actionButton}
            />
            <Button
              title="Use crop"
              loading={cropping}
              onPress={handleUseCrop}
              style={styles.actionButton}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(26, 13, 58, 0.48)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  panel: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.lg,
    maxWidth: 780,
    padding: spacing.lg,
    width: '100%',
  },
  header: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
    width: '100%',
  },
  title: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  subtitle: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
    marginTop: spacing.xs,
  },
  closeButton: {
    alignItems: 'center',
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  closeText: {
    color: colors.text,
    fontSize: 24,
    fontWeight: '700',
  },
  cropFrame: {
    backgroundColor: colors.text,
    maxWidth: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  webCropFrame: {
    cursor: 'grab',
    touchAction: 'none',
    userSelect: 'none',
  } as unknown as ViewStyle,
  cropImage: {
    position: 'absolute',
  },
  cropBorder: {
    borderColor: colors.primary,
    borderWidth: 3,
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  fallbackButton: {
    alignSelf: 'flex-start',
  },
  fallbackNotice: {
    alignItems: 'flex-start',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.md,
    width: '100%',
  },
  fallbackText: {
    color: colors.text,
    fontSize: 14,
    lineHeight: 20,
  },
  zoomRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'center',
  },
  zoomButton: {
    minWidth: 64,
  },
  zoomText: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
    minWidth: 52,
    textAlign: 'center',
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    width: '100%',
  },
  actionButton: {
    flex: 1,
  },
});
